import { cn } from '@quickmd-llc/design-system'
import { ServiceInfoForm } from '../service-info-form/ServiceInfoForm'
import { SupportedStatesDisplay } from '../../../../components/supported-states-display/SupportedStatesDisplay'
import { SupportedStatesInteractive } from '../../../../components/supported-states-display/SupportedStatesInteractive'
import {
  SHOW_SERVICE_SUPPORTED_STATES,
  SHOW_SERVICE_IMAGE_UPLOAD,
} from '../../../../constants/unreleasedServiceFeatures'

type Props = {
  isEditMode: boolean
  serviceName: string
  serviceNameError: string
  onSetServiceName: (value: string) => void
  serviceAbbreviation: string
  serviceAbbreviationError: string
  onSetServiceAbbreviation: (value: string) => void
  description: string
  descriptionLocalError: string
  isDescriptionError: boolean
  onDescriptionChange: (value: string) => void
  imageUrl: string
  onImageChange: (file: File) => void
  excludedStateCodes: string[]
  onExcludedStateCodesChange: (codes: string[]) => void
}

/**
 * Service-information block. Edit mode renders the editable ServiceInfoForm (plus the flag-gated
 * SupportedStatesInteractive); view mode renders the read-only description card (plus the flag-gated
 * image and SupportedStatesDisplay). Both flag-gated branches stay flag-off and render exactly as before.
 *
 * SCSS → Tailwind mapping ($border-color = var(--border-card) = shade[10]):
 *   .ns-service-card -> bg-shade-10 border border-shade-10 rounded-large overflow-hidden
 *                       flex flex-col gap-px
 *   .ns-card-header  -> bg-white py-sp-2.5 px-sp-3 flex items-center justify-between
 *   .ns-card-title   -> text-size-5 font-semibold leading-7 text-text-dark
 *   .ns-card-body    -> bg-white p-sp-3
 *   .vs-info-card        -> bg-white border border-shade-10 rounded-large overflow-hidden
 *   .vs-info-card-header -> py-sp-2.5 px-sp-3
 *   .vs-info-card-title  -> text-size-5 font-semibold leading-7 text-text-dark
 *   .vs-card-divider     -> h-px bg-shade-10
 *   .vs-info-main        -> flex gap-sp-3 items-start
 *   .vs-service-img      -> w-[150px] h-[150px] object-contain rounded-medium shrink-0
 *                           mt-sp-2.5 mb-sp-2.5 ml-sp-3 border border-shade-10
 *                           (150px image has no DS sizing token -> arbitrary w/h-[150px])
 *   .vs-info-content     -> flex-1 min-w-0
 *   .vs-info-card-body   -> py-sp-2.5 px-sp-3 flex flex-col gap-sp-0.5
 *   .vs-field-label      -> text-size-2 font-semibold leading-4 text-text-light
 *   .vs-field-value      -> text-size-3 font-normal leading-5 text-text-dark
 */
export const ServiceInfoSection = ({
  isEditMode,
  serviceName,
  serviceNameError,
  onSetServiceName,
  serviceAbbreviation,
  serviceAbbreviationError,
  onSetServiceAbbreviation,
  description,
  descriptionLocalError,
  isDescriptionError,
  onDescriptionChange,
  imageUrl,
  onImageChange,
  excludedStateCodes,
  onExcludedStateCodesChange,
}: Props) => {
  if (isEditMode) {
    return (
      <div className='flex flex-col gap-px overflow-hidden rounded-large border border-shade-10 bg-shade-10'>
        <div className='flex items-center justify-between bg-white px-sp-3 py-sp-2.5'>
          <span className='text-size-5 font-semibold leading-7 text-text-dark'>Service information</span>
        </div>
        <div className='bg-white p-sp-3'>
          <ServiceInfoForm
            serviceName={serviceName}
            onSetServiceName={onSetServiceName}
            serviceNameError={serviceNameError}
            serviceAbbreviation={serviceAbbreviation}
            onSetServiceAbbreviation={onSetServiceAbbreviation}
            serviceAbbreviationError={serviceAbbreviationError}
            description={description}
            onDescriptionChange={onDescriptionChange}
            descriptionLocalError={descriptionLocalError}
            isDescriptionError={isDescriptionError}
            imageUrl={imageUrl}
            onImageChange={onImageChange}
          />
        </div>
        {SHOW_SERVICE_SUPPORTED_STATES ?
          <div className='bg-white p-sp-3'>
            <SupportedStatesInteractive excludedStateCodes={excludedStateCodes} onChange={onExcludedStateCodesChange} />
          </div>
        : null}
      </div>
    )
  }

  return (
    <div className='overflow-hidden rounded-large border border-shade-10 bg-white'>
      <div className='px-sp-3 py-sp-2.5'>
        <span className='text-size-5 font-semibold leading-7 text-text-dark'>Service information</span>
      </div>
      <div className='h-px bg-shade-10' />
      <div className='flex items-start gap-sp-3'>
        {SHOW_SERVICE_IMAGE_UPLOAD && imageUrl ?
          <img
            src={imageUrl}
            alt={serviceName}
            className='mb-sp-2.5 ml-sp-3 mt-sp-2.5 h-[150px] w-[150px] shrink-0 rounded-medium border border-shade-10 object-contain'
          />
        : null}
        <div className='min-w-0 flex-1'>
          <div className='flex flex-col gap-sp-0.5 px-sp-3 py-sp-2.5'>
            <span className='text-size-2 font-semibold leading-4 text-text-light'>Description</span>
            <span className='text-size-3 font-normal leading-5 text-text-dark'>{description}</span>
          </div>
          {SHOW_SERVICE_SUPPORTED_STATES ?
            <>
              <div className='h-px bg-shade-10' />
              <div className='flex flex-col gap-sp-0.5 px-sp-3 py-sp-2.5'>
                <SupportedStatesDisplay
                  excludedStateCodes={excludedStateCodes}
                  titleClassName={cn('text-size-2 font-semibold leading-4 text-text-light')}
                />
              </div>
            </>
          : null}
        </div>
      </div>
    </div>
  )
}
